import { BasketCustomerOfferEntity, BasketCustomerOrderEntity } from '@genial/services';
import { useBreakpointValue, Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { batchTicketCustomerBasketOrderDetailsQuery } from '../../../../store/batchTicketDetail/selector';
import {
  OrderAccordion,
  OrderAccordionTypes,
} from '../../../OrderHistoryModal/components/OrderAccordion';
import {
  getBatchTicketAssetsColumns,
  getBatchTicketDetailsItems,
  getBatchTicketExtraDetailsItems,
  getBatchTicketPreDetailsItems,
} from '../BatchDetails/constants';

export interface BatchCustomerAccordionProps {
  isMobile: boolean;
  item: BasketCustomerOrderEntity;
  totalRequestedAmount: number;
  handleShowErrorToast: (errorToShow: Error) => void;
  isLonely: boolean;
}

export const BatchCustomerAccordion: React.FC<BatchCustomerAccordionProps> = ({
  item,
  isLonely,
  totalRequestedAmount,
  isMobile,
  handleShowErrorToast,
}) => {
  const viewportType = useBreakpointValue({ base: 'mobile', sm: 'tablet', lg: 'desktop' }) as
    | 'mobile'
    | 'tablet'
    | 'desktop';
  const [alreadyExpanded, setAlreadyExpanded] = React.useState(isLonely);

  const { data: customerBasketOrderDetailsData, error } = useRecoilQuery(
    batchTicketCustomerBasketOrderDetailsQuery({
      basketCustomerId: item.basketCustomerOrderId,
    }),
    { cachePolicy: 'no-cache', if: () => alreadyExpanded }
  );

  const handleUpdateAccordionState = (isOpen: boolean) => {
    if (alreadyExpanded) {
      return;
    }
    setAlreadyExpanded(isOpen);
  };

  const alreadyLoadedDetails = alreadyExpanded && !!customerBasketOrderDetailsData?.data;

  const customerOffers =
    customerBasketOrderDetailsData?.data.customerOffers || item.customerOffers || [];

  useEffect(() => {
    if (!error) {
      return;
    }
    handleShowErrorToast(error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <OrderAccordion
      isMobile={isMobile}
      startAccordionOpen={isLonely}
      updateCurrentOpenState={handleUpdateAccordionState}
      clientAccount={`${item.customer.account.accountNumber}-${item.customer.account.accountDigit}`}
      clientName={item.customer.customerName || '--'}
      lastUpdateDate={Utils.Mask.DateFormat(item.updateDate, 'dd/MM/yyyy HH:mm:ss')}
      totalRequestedAmount={Utils.Mask.Money(totalRequestedAmount)}
      assets={(customerOffers || []).map(
        (customerOffer) =>
          ({
            assetTypeLabel: customerOffer.marketTypeTranslated || 'Desconhecido',
            requestedAmount: Utils.Mask.Money(customerOffer.totalMarketRequestedAmount),
            orders: customerOffer.basketOffers || [],
            assetExtraInfoGetter: (order) =>
              getBatchTicketExtraDetailsItems(order, alreadyLoadedDetails),
            assetInfoGetter: (order) => getBatchTicketDetailsItems(order, alreadyLoadedDetails),
            assetPreInfoGetter: (order) => getBatchTicketPreDetailsItems(order),
            assetInfoColumnsGetter: (order) => getBatchTicketAssetsColumns(order, viewportType),
          } as OrderAccordionTypes.Props<[BasketCustomerOfferEntity]>['assets'][0])
      )}
    />
  );
};
